import { Component, inject, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  DataConnection,
  DataConnectionType,
} from '@nimmsta/ng-commons-lib/models';
import { TranslatePipe } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { MessageModule } from 'primeng/message';
import { catchError, map, of } from 'rxjs';
import { SortingConfiguration } from '../../../models/sortingDataConnection';
import { DataConnectionService } from '../../../services/dataConnection.service';
import { DataConnectionConfigurationComponent } from '../../dataconnection-configuration.component';

@Component({
  selector: 'app-sorting-form',
  templateUrl: './sorting-form.component.html',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    DataConnectionConfigurationComponent,
    MessageModule,
  ],
})
export class SortingFormComponent implements OnInit {
  @Input() value = {} as DataConnection;
  @Input() isTemplate = false;

  private readonly fb = inject(FormBuilder).nonNullable;
  private readonly service = inject(DataConnectionService);
  configurationError = false;
  readonly form = this.fb.group({
    name: [
      '',
      [Validators.required, Validators.minLength(2), Validators.maxLength(128)],
    ],
    identifier: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(128),
        Validators.pattern(/^[a-zA-Z0-9-]+$/),
      ],
    ],
    description: [''],
    configuration: this.fb.group(
      {
        keyField: [
          '',
          [
            Validators.required,
            Validators.pattern(/\S/),
            Validators.maxLength(255),
          ],
        ],
        validityDays: [
          7,
          [
            Validators.required,
            Validators.min(1),
            Validators.max(365),
            Validators.pattern(/^\d+$/),
          ],
        ],
        csv: ['{}', [this.validCsvJson]],
      },
      { updateOn: 'blur' },
    ),
  });

  ngOnInit(): void {
    if (this.isTemplate) {
      this.form.controls.identifier.clearValidators();
    }
    this.form.patchValue({
      name: this.value.name ?? '',
      identifier: this.value.identifier ?? '',
      description: this.value.description ?? '',
    });
    try {
      const configuration =
        typeof this.value.configuration === 'string'
          ? JSON.parse(this.value.configuration.trim() || '{}')
          : this.value.configuration;
      if (
        configuration != null &&
        (typeof configuration !== 'object' || Array.isArray(configuration))
      ) {
        throw new Error('Invalid sorting configuration');
      }
      if (configuration) {
        const config = configuration as Partial<SortingConfiguration>;
        this.form.controls.configuration.patchValue({
          keyField: config.keyField ?? '',
          validityDays: config.validityDays ?? 7,
          csv: JSON.stringify(
            Object.fromEntries(
              Object.entries(config).filter(
                ([key]) => key !== 'keyField' && key !== 'validityDays',
              ),
            ),
            null,
            2,
          ),
        });
      }
    } catch {
      this.configurationError = true;
      this.form.addValidators(() => ({ invalidConfiguration: true }));
      this.form.updateValueAndValidity();
    }
    this.form.controls.configuration.addAsyncValidators(() =>
      this.service
        .validate(
          DataConnectionType.SortingDataConnection,
          JSON.stringify(this.getConfiguration()),
        )
        .pipe(
          map(() => null),
          catchError(() => of({ invalidCsvConfiguration: true })),
        ),
    );
    this.form.controls.configuration.updateValueAndValidity();
  }

  getConfiguration(): SortingConfiguration {
    const { keyField, validityDays, csv } =
      this.form.controls.configuration.getRawValue();
    return { ...JSON.parse(csv.trim() || '{}'), keyField, validityDays };
  }

  private validCsvJson(control: AbstractControl) {
    try {
      const value: unknown = JSON.parse(control.value.trim() || '{}');
      return value && typeof value === 'object' && !Array.isArray(value)
        ? null
        : { 'dataconnection.invalid': true };
    } catch {
      return { 'dataconnection.invalid': true };
    }
  }
}
